import React from 'react';
import { QuestionBreakdown, CEFRLevel } from '../services/placementTestService';

const getLevelColor = (level: CEFRLevel): string => {
    const colors: Record<CEFRLevel, string> = {
        A1: '#22c55e', A2: '#84cc16', B1: '#eab308',
        B2: '#f97316', C1: '#ef4444', C2: '#dc2626',
    };
    return colors[level];
};

interface Props {
    breakdown: QuestionBreakdown[];
    showBreakdown: boolean;
    onToggle: () => void;
    t: (key: string, opts?: Record<string, unknown>) => string;
    locale: string;
}

const QuestionBreakdownSection: React.FC<Props> = ({
    breakdown, showBreakdown, onToggle, t, locale,
}) => {
    return (
        <div className="question-breakdown-section">
            <div className="breakdown-header">
                <h3>{t('result.questionBreakdown')}</h3>
                <p className="breakdown-desc">{t('result.questionBreakdownDesc')}</p>
                <button
                    className="breakdown-toggle"
                    onClick={onToggle}
                >
                    {showBreakdown ? t('result.hideBreakdown') : t('result.showBreakdown')}
                    <span className={`breakdown-chevron ${showBreakdown ? 'breakdown-chevron--open' : ''}`}>
                        &#9662;
                    </span>
                </button>
            </div>

            {showBreakdown && (
                <div className="breakdown-list">
                    {breakdown.map((q) => (
                        <div
                            key={q.index}
                            className={`breakdown-item ${q.isCorrect ? 'breakdown-item--correct' : 'breakdown-item--incorrect'}`}
                        >
                            <div className="breakdown-item-header">
                                <span className="breakdown-q-number">
                                    {t('result.question', { index: q.index + 1 })}
                                </span>
                                <span
                                    className="breakdown-cefr-tag"
                                    style={{ backgroundColor: getLevelColor(q.cefrLevel), color: '#fff' }}
                                >
                                    {q.cefrLevel}
                                </span>
                                <span className={`breakdown-status ${q.isCorrect ? 'breakdown-status--correct' : 'breakdown-status--incorrect'}`}>
                                    {q.isCorrect ? t('result.correct') : t('result.incorrect')}
                                </span>
                            </div>

                            <p className="breakdown-stem">{q.stem}</p>

                            {(q.userAnswer || q.correctAnswer) && (
                                <div className="breakdown-answers">
                                    {q.userAnswer && (
                                        <div className={`breakdown-answer ${q.isCorrect ? 'breakdown-answer--correct' : 'breakdown-answer--wrong'}`}>
                                            <span className="breakdown-answer-label">{t('result.yourAnswer')}</span>
                                            <span className="breakdown-answer-text">{q.userAnswer}</span>
                                        </div>
                                    )}
                                    {!q.isCorrect && q.correctAnswer && (
                                        <div className="breakdown-answer breakdown-answer--correct">
                                            <span className="breakdown-answer-label">{t('result.correctAnswer')}</span>
                                            <span className="breakdown-answer-text">{q.correctAnswer}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {q.knowledgePoints.length > 0 && (
                                <div className="breakdown-kps">
                                    {q.knowledgePoints.map((kp, ki) => {
                                        const descriptor = q.isCorrect
                                            ? (kp.masteryDescriptor[locale] || kp.masteryDescriptor['en'] || '')
                                            : (kp.weaknessDescriptor[locale] || kp.weaknessDescriptor['en'] || '');
                                        return (
                                            <div key={ki} className={`breakdown-kp ${kp.relevance === 'primary' ? 'breakdown-kp--primary' : 'breakdown-kp--secondary'}`}>
                                                <div className="breakdown-kp-header">
                                                    <span className="breakdown-kp-term">{kp.term}</span>
                                                    <span className="breakdown-kp-category">
                                                        {t(`result.knowledgeCategory.${kp.category}`) || kp.category}
                                                    </span>
                                                    <span
                                                        className="breakdown-kp-level"
                                                        style={{ color: getLevelColor(kp.cefrLevel) }}
                                                    >
                                                        {kp.cefrLevel}
                                                    </span>
                                                </div>
                                                {descriptor && (
                                                    <p className={`breakdown-kp-desc ${q.isCorrect ? 'breakdown-kp-desc--mastered' : 'breakdown-kp-desc--weak'}`}>
                                                        {q.isCorrect ? t('result.mastered') : t('result.needsWork')}: {descriptor}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default QuestionBreakdownSection;
